import { useState } from "react";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { ScreenLightbox } from "./ScreenLightbox";
import { useLang } from "@/i18n/LanguageContext";

const TEXTS = {
  de: {
    kicker: "Echte App-Einblicke",
    title: "So sieht Mira wirklich aus",
    sub: "Keine Mockups – das sind Original-Screenshots aus der Mira-App, genau so im App Store und bei Google Play.",
    captions: {
      home: "Dein Start: Willkommen zurück bei Mira",
      anlass: "Anlass wählen – Business bis Festlich",
      result: "Ehrliches Ergebnis mit Wirkungs-Stufe",
      herren: "Auch für Ihn: die Herren-Boutique mit Mira",
      categories: "Sechs Kategorien, liebevoll ehrlich",
      tryon: "Anprobe im Geschäft: Teile wählen & anprobieren",
      "herren-tryon": "Herren-Anprobe: Sortiment wählen & anprobieren",
      vergleich: "Outfit-Vergleich: Mira entscheidet",
    },
  },
  en: {
    kicker: "Real app screens",
    title: "This is what Mira really looks like",
    sub: "No mockups – these are original screenshots from the Mira app, exactly as on the App Store and Google Play.",
    captions: {
      home: "Your start: welcome back to Mira",
      anlass: "Pick the occasion – business to festive",
      result: "Honest result with radiance level",
      herren: "For him too: the men's boutique with Mira",
      categories: "Six categories, lovingly honest",
      tryon: "In-store try-on: pick pieces & try them on",
      "herren-tryon": "Men's try-on: pick from the collection",
      vergleich: "Outfit battle: Mira decides",
    },
  },
  es: {
    kicker: "La app de verdad",
    title: "Así es Mira realmente",
    sub: "Sin mockups: capturas originales de la app Mira, tal como en el App Store y Google Play.",
    captions: {
      home: "Tu inicio: bienvenida de nuevo a Mira",
      anlass: "Elige la ocasión – de business a festivo",
      result: "Resultado honesto con nivel de efecto",
      herren: "También para él: la boutique masculina con Mira",
      categories: "Seis categorías, honestas con cariño",
      tryon: "Prueba en tienda: elige piezas y pruébatelas",
      "herren-tryon": "Prueba para él: elige del catálogo",
      vergleich: "Duelo de outfits: Mira decide",
    },
  },
  it: {
    kicker: "La vera app",
    title: "Ecco come è davvero Mira",
    sub: "Niente mockup: screenshot originali dell'app Mira, esattamente come su App Store e Google Play.",
    captions: {
      home: "Il tuo inizio: bentornata da Mira",
      anlass: "Scegli l'occasione – dal business al festivo",
      result: "Risultato onesto con livello di splendore",
      herren: "Anche per lui: la boutique uomo con Mira",
      categories: "Sei categorie, oneste con amore",
      tryon: "Prova in negozio: scegli i capi e provali",
      "herren-tryon": "Prova per lui: scegli dal catalogo",
      vergleich: "Sfida di outfit: decide Mira",
    },
  },
  fr: {
    kicker: "La vraie app",
    title: "Voici à quoi ressemble vraiment Mira",
    sub: "Pas de mockups : des captures originales de l'app Mira, exactement comme sur l'App Store et Google Play.",
    captions: {
      home: "Ton départ : bon retour chez Mira",
      anlass: "Choisis l'occasion – business à festif",
      result: "Résultat honnête avec niveau d'éclat",
      herren: "Pour lui aussi : la boutique homme avec Mira",
      categories: "Six catégories, honnêtes avec amour",
      tryon: "Essayage en boutique : choisis des pièces et essaie-les",
      "herren-tryon": "Essayage pour lui : choisis dans le catalogue",
      vergleich: "Duel d'outfits : Mira décide",
    },
  },
};

const SCREENS = ["home", "anlass", "result", "herren", "categories", "tryon", "herren-tryon", "vergleich"];

export default function AppScreens() {
  const { lang } = useLang();
  const t = TEXTS[lang] || TEXTS.de;
  const [idx, setIdx] = useState(null);

  return (
    <section id="app-einblicke" className="py-24 sm:py-32 overflow-hidden" data-testid="app-screens-section">
      <Reveal className="mx-auto max-w-7xl px-5 sm:px-8 text-center">
        <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
          <Star size={11} /> {t.kicker}
        </p>
        <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="app-screens-title">
          {t.title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base md:text-lg" style={{ color: "var(--muted-warm)" }}>{t.sub}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 pb-6 sm:px-8 lg:justify-center" data-testid="app-screens-row" style={{ scrollbarWidth: "thin" }}>
          {SCREENS.map((key, i) => (
            <figure key={key} className="shrink-0 snap-center" style={{ width: 210 }} data-testid={`app-screen-${key}`}>
              <button
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`app-screen-zoom-${key}`}
                aria-label={t.captions[key]}
                className="block w-full cursor-zoom-in overflow-hidden rounded-[30px] border-[6px] shadow-xl transition-transform duration-500 hover:-translate-y-2"
                style={{ borderColor: "var(--on-surface)", background: "var(--on-surface)", transform: `rotate(${i % 2 === 0 ? "-0.6deg" : "0.6deg"})` }}
              >
                <img
                  src={`/app-screens/${lang}/${key}.webp?v=5`}
                  alt={t.captions[key]}
                  loading="lazy"
                  width={640}
                  height={1389}
                  className="w-full rounded-[24px]"
                />
              </button>
              <figcaption className="mt-3.5 text-center text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }}>
                {t.captions[key]}
              </figcaption>
            </figure>
          ))}
        </div>
      </Reveal>

      <ScreenLightbox screens={SCREENS} captions={t.captions} idx={idx} setIdx={setIdx} />
    </section>
  );
}
